import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCampaign } from "@/contexts/CampaignContext";

interface DayMetrics {
  day_number: number;
  title: string;
  body_markdown: string;
  exercise_markdown: string;
  tip_markdown: string;
  opens: number;
  clicks: number;
  openRate: number;
  clickRate: number;
}

export default function CampaignDays() {
  console.log('CampaignDays component rendered');
  const { selectedCampaignId, selectedCampaign } = useCampaign();

  const [selectedDay, setSelectedDay] = useState<DayMetrics | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-days', selectedCampaignId],
    enabled: !!selectedCampaignId,
    queryFn: async () => {
      if (!selectedCampaignId) return null;

      const total = selectedCampaign?.total_days || 21;

      // Get active subscribers count for this campaign
      const { count } = await supabase
        .from("subscribers")
        .select("*", { count: "exact", head: true })
        .eq("campaign_id", selectedCampaignId)
        .eq("status", "confirmed");

      const activeSubscribers = count || 0;

      // Get subscriber IDs for this campaign
      const { data: campaignSubscribers } = await supabase
        .from("subscribers")
        .select("id")
        .eq("campaign_id", selectedCampaignId);

      const subscriberIds = campaignSubscribers?.map(s => s.id) || [];

      // Get campaign days for the selected campaign
      const { data: campaignDays } = await supabase
        .from("campaign_days")
        .select("*")
        .eq("campaign_id", selectedCampaignId)
        .order("day_number");

      // Get engagement events grouped by day (filtered by campaign subscribers)
      const { data: emailLogs } = subscriberIds.length > 0 ? await supabase
        .from("email_logs")
        .select("day_number, subscriber_id")
        .in("subscriber_id", subscriberIds) : { data: [] };

      const { data: events } = subscriberIds.length > 0 ? await supabase
        .from("engagement_events")
        .select("event_type, subscriber_id")
        .in("subscriber_id", subscriberIds) : { data: [] };

      // Create a map of email logs to track which subscribers received which days
      const daySubscribers = new Map<number, Set<string>>();
      emailLogs?.forEach((log) => {
        if (!daySubscribers.has(log.day_number)) {
          daySubscribers.set(log.day_number, new Set());
        }
        daySubscribers.get(log.day_number)!.add(log.subscriber_id);
      });

      // Count events by subscriber
      const subscriberEvents = new Map<string, { opens: number; clicks: number }>();
      events?.forEach((event) => {
        if (!subscriberEvents.has(event.subscriber_id)) {
          subscriberEvents.set(event.subscriber_id, { opens: 0, clicks: 0 });
        }
        const stats = subscriberEvents.get(event.subscriber_id)!;
        if (event.event_type === "open") stats.opens++;
        if (event.event_type === "click") stats.clicks++;
      });

      // Build metrics for each day
      const metricsData: DayMetrics[] = [];
      for (let i = 1; i <= total; i++) {
        const dayData = campaignDays?.find((d) => d.day_number === i);
        const sentToSubscribers = daySubscribers.get(i)?.size || 0;
        
        let opens = 0;
        let clicks = 0;
        
        // Count opens and clicks for this day's subscribers
        daySubscribers.get(i)?.forEach((subId) => {
          const stats = subscriberEvents.get(subId);
          if (stats) {
            opens += stats.opens;
            clicks += stats.clicks;
          }
        });

        metricsData.push({
          day_number: i,
          title: dayData?.title || "",
          body_markdown: dayData?.body_markdown || "",
          exercise_markdown: dayData?.exercise_markdown || "",
          tip_markdown: dayData?.tip_markdown || "",
          opens,
          clicks,
          openRate: sentToSubscribers > 0 ? (opens / sentToSubscribers) * 100 : 0,
          clickRate: sentToSubscribers > 0 ? (clicks / sentToSubscribers) * 100 : 0,
        });
      }

      return {
        days: metricsData,
        totalDays: total,
        activeSubscribers,
      };
    },
  });

  const days = data?.days || [];
  const totalDays = data?.totalDays || 21;
  const activeSubscribers = data?.activeSubscribers || 0;

  const saveMutation = useMutation({
    mutationFn: async (dayData: DayMetrics) => {
      if (!selectedCampaignId) {
        throw new Error("No campaign selected");
      }

      const { error } = await supabase
        .from("campaign_days")
        .upsert({
          day_number: dayData.day_number,
          title: dayData.title,
          body_markdown: dayData.body_markdown,
          exercise_markdown: dayData.exercise_markdown,
          tip_markdown: dayData.tip_markdown,
          campaign_id: selectedCampaignId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Day content saved successfully",
      });
      setIsEditing(false);
      setSelectedDay(null);
      queryClient.invalidateQueries({ queryKey: ['campaign-days'] });
    },
    onError: (error: any) => {
      console.error("Error saving:", error);
      toast({
        title: "Error",
        description: "Failed to save day content",
        variant: "destructive",
      });
    },
  });

  const testSendMutation = useMutation({
    mutationFn: async ({ dayNumber, email }: { dayNumber: number; email: string }) => {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          testEmail: email,
          dayNumber: dayNumber,
          campaignId: selectedCampaignId,
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || "Failed to send test email");
      }

      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Test Email Sent",
        description: data.message,
      });
      setTestEmail("");
    },
    onError: (error: any) => {
      console.error("Test send error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      });
    },
  });

  const handleSave = async () => {
    if (!selectedDay) return;

    // Validate required fields
    if (!selectedDay.title.trim()) {
      toast({
        title: "Validation Error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }

    if (!selectedDay.body_markdown.trim()) {
      toast({
        title: "Validation Error",
        description: "Main Content is required",
        variant: "destructive",
      });
      return;
    }

    saveMutation.mutate(selectedDay);
  };

  const handleTestSend = () => {
    if (!selectedDay || !testEmail.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a test email address",
        variant: "destructive",
      });
      return;
    }

    testSendMutation.mutate({
      dayNumber: selectedDay.day_number,
      email: testEmail.trim(),
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-2xl md:text-3xl font-bold">Campaign Days</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Active Subscribers: {activeSubscribers}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {days.map((day) => {
          // Subtle header colors that cycle through
          const colorIndex = (day.day_number - 1) % 5;
          const headerClasses = [
            "bg-slate-50 dark:bg-slate-800/50",
            "bg-zinc-50 dark:bg-zinc-800/50",
            "bg-gray-50 dark:bg-gray-800/50",
            "bg-neutral-50 dark:bg-neutral-800/50",
            "bg-stone-50 dark:bg-stone-800/50",
          ][colorIndex];

          return (
            <Card
              key={day.day_number}
              className="cursor-pointer transition-all hover:shadow-lg overflow-hidden"
              onClick={() => {
                setSelectedDay(day);
                setIsEditing(true);
              }}
            >
              <CardHeader className={`pb-3 ${headerClasses}`}>
                <CardTitle className="text-base sm:text-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span>Day {day.day_number}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-lg sm:text-2xl">📧</span>
                      <span className="font-medium">{day.openRate.toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-lg sm:text-2xl">🖱️</span>
                      <span className="font-medium">{day.clickRate.toFixed(1)}%</span>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm line-clamp-2 text-foreground">
                  {day.title}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Day {selectedDay?.day_number}</DialogTitle>
          </DialogHeader>
          
          {selectedDay && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={selectedDay.title}
                  onChange={(e) =>
                    setSelectedDay({ ...selectedDay, title: e.target.value })
                  }
                  placeholder="Email subject line"
                  required
                />
              </div>

              <div>
                <Label htmlFor="body">Main Content *</Label>
                <Textarea
                  id="body"
                  value={selectedDay.body_markdown}
                  onChange={(e) =>
                    setSelectedDay({ ...selectedDay, body_markdown: e.target.value })
                  }
                  placeholder="Main email content"
                  rows={8}
                  required
                />
              </div>

              <div>
                <Label htmlFor="exercise">Exercise</Label>
                <Textarea
                  id="exercise"
                  value={selectedDay.exercise_markdown}
                  onChange={(e) =>
                    setSelectedDay({ ...selectedDay, exercise_markdown: e.target.value })
                  }
                  placeholder="Optional exercise content"
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="tip">Tip</Label>
                <Textarea
                  id="tip"
                  value={selectedDay.tip_markdown}
                  onChange={(e) =>
                    setSelectedDay({ ...selectedDay, tip_markdown: e.target.value })
                  }
                  placeholder="Optional tip content"
                  rows={4}
                />
              </div>

              {/* Test Send Section */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Label htmlFor="testEmail" className="text-sm font-medium">
                    Test Send
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    Send a test email to preview this day's content
                  </span>
                </div>
                <div className="flex gap-2">
                  <Input
                    id="testEmail"
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="Enter test email address"
                    className="flex-1"
                  />
                  <Button
                    onClick={handleTestSend}
                    disabled={testSendMutation.isPending || !testEmail.trim()}
                    variant="outline"
                    size="sm"
                  >
                    {testSendMutation.isPending ? "Sending..." : "Send Test"}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedDay(null);
                    setTestEmail("");
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
